import { UseQueryResult } from '@tanstack/react-query';
import { useApplications } from 'hooks/ReactQueryHooks/useApplicationFromQuery';
import { useFilters } from 'hooks/useFilters';
import { useState, createContext, useContext, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { QueryParamsInterface } from 'types/Shared';

interface IApplicationsListContext {
  data: any;
  tablePagination: any;
  showFilterModal: boolean;
  filterBadges: any;
  toggleFilterModal: () => void;
  applyFiltersHandler: (
    filters: QueryParamsInterface,
    filterBadgesData?: {
      [key: string]: { label: string; value: string | number };
    }
  ) => void;
  removeFilterHandler: (filterKey: string) => void;
  fetchApplications: UseQueryResult<any, any>;
}

const defaultContextValue: any = {
  data: {},
  tablePagination: {},
  filterBadges: {},
};

const ApplicationsListContext =
  createContext<IApplicationsListContext>(defaultContextValue);

const useApplicationsListProvider = (): IApplicationsListContext => {
  const { applicationFormId } = useParams<string>();
  const [params, setParams] = useState({
    page: 1,
    perPage: 10,
  });

  const {
    showFilterModal,
    appliedFilters,
    filterBadges,
    toggleFilterModal,
    applyFiltersHandler,
    removeFilterHandler,
  } = useFilters();

  const fetchApplications = useApplications({
    formId: applicationFormId!,
    params: { ...params, ...appliedFilters },
  });
  const data = fetchApplications?.data;
  const dataCount = data?.meta?.count;
  const totalPage = Math.ceil(Number(dataCount) / params.perPage);

  const nextPageHandler = () => {
    if (params.page < totalPage) {
      setParams((prevState) => {
        return {
          ...prevState,
          page: prevState.page + 1,
        };
      });
    }
  };

  const previousPageHandler = () => {
    if (params.page > 1) {
      setParams((prevState) => {
        return {
          ...prevState,
          page: prevState.page - 1,
        };
      });
    }
  };

  const perPageHandler = (perPage: number) => {
    setParams((prevState) => {
      return {
        ...prevState,
        page: 1,
        perPage,
      };
    });
  };

  const tablePagination = {
    page: params.page,
    pageSize: params.perPage,
    pageCount: totalPage,
    currentPage: params.page,
    setPageSize: perPageHandler,
    nextPage: nextPageHandler,
    previousPage: previousPageHandler,
  };

  return {
    data,
    fetchApplications,
    tablePagination,
    showFilterModal,
    filterBadges,
    toggleFilterModal,
    applyFiltersHandler,
    removeFilterHandler,
  };
};

interface IApplicationsListProviderProps {
  children: React.ReactNode;
}

export const ApplicationsListProvider = ({
  children,
}: IApplicationsListProviderProps) => {
  const value = useApplicationsListProvider();
  return (
    <ApplicationsListContext.Provider value={value}>
      {children}
    </ApplicationsListContext.Provider>
  );
};

export const useApplicationsListContext = () =>
  useContext(ApplicationsListContext);
